// coInternKatalog.ts — die vier courté-eigenen Geschäftsbeziehungen.
//
// Gegenstück zu `apps/api/src/coIntern.ts`. Beide Listen müssen ZEICHENGENAU
// übereinstimmen: Die Maske schickt die Arten als „a; b"-Text an
// `PUT /contact/:id`, und das Backend wirft jeden Wert weg, der nicht im
// Katalog steht — der Haken wäre gesetzt, nach dem Neuladen wieder weg, ohne
// Fehlermeldung. Ein fehlendes Leerzeichen in „Sponsoring Partner" reicht
// dafür. Abgesichert ist die Übereinstimmung durch `coIntern.spec.ts` im
// Backend (Textvergleich über die Paketgrenze, wie bei
// `contactStatusGruppe.spec.ts`).
//
// Die Werte sind zugleich die Namen der gleichlautenden `ContactStatus`-
// Einträge. Das ist Absicht und keine Doppelung: Wer einen Kontakt in der
// Anlage-Maske als „Lieferant" erfasst, hat ihn fachlich als CO-Kontakt
// erfasst, und die Schutzregel erkennt beide Wege.
//
// Bewusst ohne React/DOM, damit die Regel per Vitest prüfbar ist — wie
// `sonstigeFilterModel.ts`.

/** Die vier Arten in Anzeigereihenfolge. */
export const CO_ARTEN: string[] = [
  "Lieferant",
  "Sponsoring Partner",
  "Mitgliedschaft/Verband",
  "Direktionspartner",
];

/** Trennzeichen der „a; b"-Liste — wie bei `kontaktherkunft`. */
export const CO_ARTEN_TRENNER = "; ";

/** Die „a; b"-Liste als Array — leere Teile und Leerraum fallen weg. */
export const coArtenLesen = (wert?: string | null): string[] =>
  (wert ?? "")
    .split(";")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

/**
 * Ein Array als „a; b"-Liste, in Katalogreihenfolge und ohne Dubletten.
 *
 * Reihenfolge des Katalogs, nicht des Klickens: Sonst unterscheiden sich zwei
 * Kontakte mit denselben Arten im gespeicherten Text, und die
 * Änderungserkennung der Maske (`nurGeaenderteFelder`) schlägt grundlos an.
 *
 * Nichts übrig ⇒ `null`, nicht `""`. Ein leerer String wäre ein GESETZTER
 * Wert: Der Kontakt zählte weiter als CO-intern und bliebe für die Kollegen
 * unsichtbar, obwohl alle Haken entfernt sind.
 */
export const coArtenSchreiben = (arten: readonly string[]): string | null => {
  const gewollt = new Set(arten.map((a) => a.trim()));
  const sortiert = CO_ARTEN.filter((a) => gewollt.has(a));
  return sortiert.length ? sortiert.join(CO_ARTEN_TRENNER) : null;
};

/**
 * Die CO-Arten einer Tabellenzeile — aus `coArten` ODER aus dem Kontaktstatus.
 *
 * Das Backend rechnet einen Kontakt auch dann zu „CO intern", wenn seine
 * CO-Zugehörigkeit nur am Status hängt (ein in der Anlage-Maske als
 * „Lieferant" erfasster Kontakt). Ohne diesen Rückgriff stünde er in der Liste
 * und die Typ-Spalte bliebe bei ihm leer. Der Serverfilter `CoArt` prüft aus
 * demselben Grund beide Quellen.
 */
export const coArtenDerZeile = (
  coArten?: string | null,
  kontaktStatus?: string | null,
): string[] => {
  const gepflegt = coArtenLesen(coArten);
  if (gepflegt.length) return gepflegt;
  const status = (kontaktStatus ?? "").trim();
  return CO_ARTEN.includes(status) ? [status] : [];
};

// ---------------------------------------------------------------------------
// Die Angaben der Lenkungsliste (Kategorie, Bewertung) — Anzeige-Regeln.
// ---------------------------------------------------------------------------

/**
 * Die Warengruppen der Lenkungsliste, bereinigt.
 *
 * Die Quelle führt „IT, EDV und Kommunikation" in drei Schreibweisen, die sich
 * nur an der Stelle eines weichen Zeilenumbruchs unterscheiden — im Filter
 * wären das drei Einträge, von denen jeder ein Drittel der Treffer zeigt. Der
 * Import schreibt deshalb die Fassung aus dieser Liste.
 */
export const CO_KATEGORIEN: string[] = [
  "Bürobedarf",
  "Dienstleistung",
  "Handwerk",
  "Immobilie u. Räume",
  "IT, EDV und Kommunikation",
  "Lebensmittel",
  "Mobilität",
  "QM",
  "Verband/Verein",
];

/** Farbstufen der Schulnote — die Klassennamen stehen in `contactTable.css`. */
export type BewertungsTon = "co-note-1" | "co-note-2" | "co-note-3" | "co-note-4" | "co-note-x";

/**
 * Die Zahl hinter einer Bewertung.
 *
 * Die Quelle führt neben „1.5" auch Spannen („3 - 4") und Notizen
 * („noch Bülles-IT"). Aus einer Spanne wird der Mittelwert, aus einer Notiz
 * `null` — sie bleibt lesbar, bekommt aber keine Farbe, die eine Aussage
 * vortäuscht, die niemand getroffen hat.
 */
export const bewertungsZahl = (wert?: string | null): number | null => {
  const s = (wert ?? "").trim().replace(/,/g, ".");
  if (!s) return null;
  const zahlen = s.match(/\d+(?:\.\d+)?/g);
  if (!zahlen || !/^[\d\s.,–—-]+$/.test(s)) return null;
  const werte = zahlen.map(Number).filter((n) => Number.isFinite(n) && n >= 1 && n <= 6);
  if (!werte.length) return null;
  return werte.reduce((a, b) => a + b, 0) / werte.length;
};

/**
 * Die Farbstufe zu einer Bewertung.
 *
 * Vier Stufen statt sechs: Die Liste kennt praktisch nur 1 bis 4, und ein
 * Farbverlauf über sechs Stufen unterscheidet im Augenwinkel nichts mehr.
 * Die Grenzen liegen auf den halben Noten, weil genau die vorkommen (1,5 /
 * 2,5) — eine 2,5 ist noch „gut", eine 2,6 wäre es nicht mehr.
 */
export const bewertungsTon = (wert?: string | null): BewertungsTon => {
  const n = bewertungsZahl(wert);
  if (n === null) return "co-note-x";
  if (n <= 1.5) return "co-note-1";
  if (n <= 2.5) return "co-note-2";
  if (n <= 3.5) return "co-note-3";
  return "co-note-4";
};

/** „1.5" → „1,5" — deutsches Dezimalkomma, sonst unverändert. */
export const bewertungsText = (wert?: string | null): string =>
  (wert ?? "").trim().replace(/(\d),?\.(\d)/g, "$1,$2");

/** Ein Halbjahr der Bewertungsreihe — eine Spalte der Mappe. */
export type Halbjahr = {
  /** Schlüssel in `coIntern.bewertungen`, etwa „2025-2". */
  periode: string;
  /** Spalten-Id der Tabelle, etwa „CoBew2025-2". */
  spalte: string;
  /** Erste Kopfzeile: das Jahr. */
  jahr: string;
  /** Zweite Kopfzeile, in der Schreibweise der Mappe: „2. Halbj.". */
  teil: string;
};

const halbjahr = (periode: string): Halbjahr => {
  const [jahr, nr] = periode.split("-");
  return { periode, spalte: `CoBew${periode}`, jahr, teil: `${nr}. Halbj.` };
};

/**
 * Die Bewertungsspalten der Mappe, jüngstes Halbjahr zuerst — Spalte T bis AF.
 *
 * Eine Spalte je Halbjahr, wie in der Lenkungsliste. Fest verdrahtet und nicht
 * aus den geladenen Daten abgeleitet: Die Liste ist ein QM-Nachweis, und ein
 * Halbjahr OHNE Bewertung ist dort eine Aussage. Käme die Reihe aus den Daten,
 * verschwände eine Spalte genau dann, wenn niemand mehr darin bewertet ist —
 * die Lücke wäre nicht mehr zu sehen.
 *
 * Das **2. Halbjahr 2024 fehlt**: Diese Spalte gibt es in der Mappe nicht. Sie
 * wird hier nicht erfunden, sonst zeigte die Tabelle eine Lücke, die das
 * QM-Handbuch gar nicht kennt.
 *
 * 2026 steht leer in der Mappe — die Spalten sind vorbereitet und die Stelle,
 * an der die nächste Bewertung landet.
 */
export const CO_HALBJAHRE: Halbjahr[] = [
  "2026-2",
  "2026-1",
  "2025-2",
  "2025-1",
  // „2024 - 2. Halbj." gibt es in der Mappe nicht.
  "2024-1",
  "2023-2",
  "2023-1",
  "2022-2",
  "2022-1",
  "2021-2",
  "2021-1",
  "2020-2",
  "2020-1",
].map(halbjahr);

/** „2025-2" → „2025 · 2. Halbjahr". */
export const halbjahrLabel = (periode: string): string => {
  const m = /^(\d{4})-([12])$/.exec(periode);
  return m ? `${m[1]} · ${m[2]}. Halbjahr` : periode;
};
